// Copyright (c) 2024 Goffy. This file belongs to Goffy. All Rights Reserved.

import { isLocalDateString } from './localDate';

export type HoleUpdateEvent = {
  type: 'hole.update';
  roundId: string;
  tourId?: string;
  hole: number;
  strokeIndex?: number | null;
  par?: number | null;
  entries?: Array<{
    id: string;
    playerId?: string;
    grossStrokes: number | null;
    handicapStrokes?: number | null;
    netStrokes?: number | null;
    createdAt: string;
  }>;
  totals?: Array<{ playerId?: string; totalStrokes: number; pickups: number }>;
  status?: 'accepted' | 'rejected';
  message?: string;
};

export type HoleUpdatedEvent = HoleUpdateEvent & { type: 'hole.updated' };

export type RoundStateEvent = {
  type: 'round.state';
  roundId: string;
  players?: Array<{ id: string; name: string; createdAt?: string }>;
  holes?: Array<{ holeNumber: number; par?: number; strokeIndex?: number; index?: number; name?: string; description?: string }>;
  totals?: Array<{ playerName: string; totalStrokes: number; pickups: number }>;
  holeEntries?: Array<{
    id: string;
    roundId: string;
    holeNumber: number;
    playerName: string;
    grossStrokes: number | null;
    handicapStrokes?: number | null;
    netStrokes?: number | null;
    createdAt: string;
  }>;
  holeEvents?: Array<{
    id: string;
    roundId?: string;
    playerId: string;
    holeNumber: number;
    eventType: string;
    eventValue?: string | null;
    occurredAt?: string;
  }>;
};

export type ScoreUpdateCommand = {
  type: 'score.update';
  roundId: string;
  playerId: string;
  playerName?: string;
  hole: number;
  strokes: number | null;
  pickup: boolean;
  timestamp: string;
  traceId?: string;
};

export type PlayersSnapshotEvent = {
  type: 'players_snapshot';
  players?: Array<{
    id: string;
    name: string;
    createdAt: string;
    userId?: string;
    displayName?: string;
    imageKey?: string;
    imageThumbKey?: string;
  }>;
  tourId?: string;
  Players?: Array<{
    id: string;
    name: string;
    createdAt: string;
    userId?: string;
    displayName?: string;
    imageKey?: string;
    imageThumbKey?: string;
  }>;
  TourId?: string;
};

export type PlayerProfileUpdateAck = {
  type: 'player.update.ack';
  requestId?: string;
  playerId?: string;
};

export type PlayerProfileUpdateError = {
  type: 'player.update.error';
  requestId?: string;
  message?: string;
};

export type PlayerImagePresignAck = {
  type: 'player.image.presign.ack';
  requestId?: string;
  uploadUrl?: string;
  publicUrl?: string;
};

export type PlayerImagePresignError = {
  type: 'player.image.presign.error';
  requestId?: string;
  message?: string;
};

export type BugImagePresignAck = {
  type: 'bug.image.presign.ack';
  requestId?: string;
  uploadUrl?: string;
  publicUrl?: string;
};

export type BugImagePresignError = {
  type: 'bug.image.presign.error';
  requestId?: string;
  message?: string;
};

export type BugReportAck = {
  type: 'bug.report.ack';
  requestId?: string;
};

export type BugReportError = {
  type: 'bug.report.error';
  requestId?: string;
  message?: string;
};

export type PlayersErrorEvent = {
  type: 'player_error';
  error?: string;
  Error?: string;
};

export type TourUpdateEvent = {
  type?: string;
  tourId?: string;
  TourId?: string;
  tourName?: string;
  TourName?: string;
};

export type TourCreatedEvent = {
  type: 'tour.created';
  tourId?: string;
  TourId?: string;
  requestId?: string;
  RequestId?: string;
  tourName?: string;
  TourName?: string;
  isHidden?: boolean;
  IsHidden?: boolean;
};

export type TourInitiateRejectedEvent = {
  type: 'tour.initiate.rejected';
  requestId?: string;
  code?: string;
  messageKey?: string;
  message?: string;
  rejectedAt?: string;
};

export type TourMemberSnapshot = {
  playerId: string;
  name: string;
  userId?: string | null;
  isTemporary?: boolean;
  joinedAt?: string;
};

export type PlatformPlayerSnapshot = {
  playerId: string;
  name: string;
  userId?: string | null;
  isTemporary?: boolean;
  createdAt?: string;
};

export type TourMembersSnapshotEvent = {
  type: 'tour.members.snapshot';
  requestId?: string;
  tourId?: string;
  members?: TourMemberSnapshot[];
  platformPlayers?: PlatformPlayerSnapshot[];
};

export type TourMemberAddedEvent = {
  type: 'tour.member.added';
  requestId?: string;
  tourId?: string;
  member?: TourMemberSnapshot;
};

export type TourMemberRemovedEvent = {
  type: 'tour.member.removed';
  requestId?: string;
  tourId?: string;
  playerId?: string;
  removedFromRoundIds?: string[];
  removedAt?: string;
};

export type TourMemberRemoveRejectedEvent = {
  type: 'tour.member.remove.rejected';
  requestId?: string;
  tourId?: string;
  playerId?: string;
  code?: string;
  messageKey?: string;
  message?: string;
  rejectedAt?: string;
};

export type TourMemberClaimedEvent = {
  type: 'tour.member.claimed';
  requestId?: string;
  tourId?: string;
  sourcePlayerId?: string;
  claimClientId?: string;
  startedRoundId?: string;
  claimedAt?: string;
  claimToken?: string;
};

export type TourMemberClaimRejectedEvent = {
  type: 'tour.member.claim.rejected';
  requestId?: string;
  tourId?: string;
  sourcePlayerId?: string;
  claimClientId?: string;
  code?: string;
  messageKey?: string;
  message?: string;
  rejectedAt?: string;
};

export type TourMemberClaimReleasedEvent = {
  type: 'tour.member.claim.released';
  requestId?: string;
  tourId?: string;
  claimClientId?: string;
  releasedAt?: string;
};

export type FriendSnapshot = {
  friendId: string;
  playerId: string;
  nickname?: string | null;
  name: string;
  playerName?: string | null;
  userId?: string | null;
  status?: 'linked' | 'guest';
  createdAt?: string;
  archivedAt?: string | null;
};

export type FriendListSnapshotEvent = {
  type: 'friend.list.snapshot';
  requestId?: string;
  ownerUserId?: string;
  friends?: FriendSnapshot[];
};

export type FriendAddedEvent = {
  type: 'friend.added';
  requestId?: string;
  friend?: {
    id: string;
    ownerUserId: string;
    playerId: string;
    nickname?: string | null;
    createdAt?: string;
    archivedAt?: string | null;
  };
};

export type FriendAddRejectedEvent = {
  type: 'friend.add.rejected';
  requestId?: string;
  ownerUserId?: string;
  playerId?: string;
  code?: string;
  messageKey?: string;
  message?: string;
  rejectedAt?: string;
};

export type FriendRemovedEvent = {
  type: 'friend.removed';
  requestId?: string;
  ownerUserId?: string;
  friendId?: string;
  removedAt?: string;
};

export type FriendRemoveRejectedEvent = {
  type: 'friend.remove.rejected';
  requestId?: string;
  ownerUserId?: string;
  friendId?: string;
  code?: string;
  messageKey?: string;
  message?: string;
  rejectedAt?: string;
};

export type FriendUpdatedEvent = {
  type: 'friend.updated';
  requestId?: string;
  friend?: {
    id: string;
    ownerUserId: string;
    playerId: string;
    nickname?: string | null;
    createdAt?: string;
    archivedAt?: string | null;
  };
};

export type FriendUpdateRejectedEvent = {
  type: 'friend.update.rejected';
  requestId?: string;
  ownerUserId?: string;
  friendId?: string;
  code?: string;
  messageKey?: string;
  message?: string;
  rejectedAt?: string;
};

export type RoundMemberRemovedEvent = {
  type: 'round.member.removed';
  requestId?: string;
  roundId?: string;
  tourId?: string;
  playerId?: string;
  removedAt?: string;
};

export type RoundMemberRemoveRejectedEvent = {
  type: 'round.member.remove.rejected';
  requestId?: string;
  roundId?: string;
  tourId?: string;
  playerId?: string;
  code?: string;
  messageKey?: string;
  message?: string;
  rejectedAt?: string;
};

export type RoundRemovedEvent = {
  type: 'round.removed';
  requestId?: string;
  roundId?: string;
  tourId?: string;
  removedAt?: string;
};

export type RoundRemoveRejectedEvent = {
  type: 'round.remove.rejected';
  requestId?: string;
  roundId?: string;
  tourId?: string;
  code?: string;
  messageKey?: string;
  message?: string;
  rejectedAt?: string;
};

export type RoundMemberAddedEvent = {
  type: 'round.member.added';
  requestId?: string;
  roundId?: string;
  tourId?: string;
  playerId?: string;
  addedAt?: string;
};

export type RoundMemberAddRejectedEvent = {
  type: 'round.member.add.rejected';
  requestId?: string;
  roundId?: string;
  tourId?: string;
  playerId?: string;
  code?: string;
  messageKey?: string;
  message?: string;
  rejectedAt?: string;
};

export type PlayerTemporaryCreatedEvent = {
  type: 'player.temporary.created';
  requestId?: string;
  playerId?: string;
  name?: string;
  createdAt?: string;
  isTemporary?: boolean;
};

export type RoundInitiateRejectedEvent = {
  type: 'round.initiate.rejected';
  requestId?: string;
  tourId?: string;
  code?: string;
  messageKey?: string;
  message?: string;
  rejectedAt?: string;
};

export type RoundCreatedEvent = {
  type: 'round.created';
  requestId?: string;
  roundId?: string;
  tourId?: string;
  gameDefinitionIds?: string[];
  course?: { id?: string; name?: string; address?: string; lat?: number; lon?: number };
  holesCount?: number;
  playDate?: string;
  createdAt?: string;
};

export type RoundUpdatedEvent = {
  type: 'round.updated';
  roundId?: string;
  gameDefinitionIds?: string[];
  gameWeights?: Array<{ gameDefinitionId?: string; weightPercent?: number }>;
  course?: { id?: string; name?: string; address?: string; lat?: number; lon?: number };
  holesCount?: number;
  playDate?: string;
  name?: string;
};

export type RoundGroupsUpdatedEvent = {
  type: 'round.groups.updated';
  requestId?: string;
  roundId?: string;
  groups?: Array<{ groupId?: string; teams?: Array<{ teamId?: string; playerIds?: string[] }> }>;
  playerOrder?: string[];
  updatedAt?: string;
};

export type RoundGroupsUpdateRejectedEvent = {
  type: 'round.groups.update.rejected';
  requestId?: string;
  roundId?: string;
  code?: string;
  messageKey?: string;
  message?: string;
  rejectedAt?: string;
};

export type RoundGroupsSwapRejectedEvent = {
  type: 'round.groups.swap.rejected';
  requestId?: string;
  roundId?: string;
  code?: string;
  messageKey?: string;
  message?: string;
  rejectedAt?: string;
};

export type GameResultPlayer = {
  playerId: string;
  lastScorePoints: number;
  holeScorePoints?: Array<{ holeNumber: number; scorePoints: number }>;
  lastHoleNumber?: number;
  lastGrossStrokes?: number;
};

export type GameResultGame = {
  gameId: string;
  gameName?: string;
  players?: GameResultPlayer[];
};

export type GameResultsEvent = {
  type: 'game.result';
  roundId: string;
  games: GameResultGame[];
  updatedPlayerId?: string;
  updatedHoleNumber?: number;
  calculatedAt?: string;
};

export type TeamCreatedEvent = {
  type: 'team.created';
  teamId?: string;
  roundId?: string;
  requestId?: string;
};

export type RoundLeaderboardEvent = {
  type: 'round.leaderboard';
  roundId: string;
  totals: Array<{
    playerId?: string;
    totalRoundGamePoints: number;
    tourGamesPoints: number;
    roundGamePoints?: Array<{ gameId?: string; roundGamePoints: number; position?: number }>;
  }>;
};

export type UserCreatedEvent = {
  type: 'user.created';
  userId?: string;
  username?: string;
  displayName?: string;
  fullName?: string;
  success?: boolean;
  error?: string;
  requestId?: string;
  queued?: boolean;
  replaces?: string;
};

export type UserInfoEvent = {
  type: 'user.info';
  userId?: string;
  forename?: string;
  createdAt?: string;
  requestId?: string;
  success?: boolean;
  error?: string;
};

export type GolfCourseChangedEvent = {
  type: 'golfCourse.changed';
  courseId?: string;
  clubId?: string;
  requestId?: string;
  name?: string;
  par?: number;
  holesCount?: number;
  action?: string;
};

export type GolfCourseUpsertRejectedEvent = {
  type: 'golfCourse.upsert.rejected';
  requestId?: string;
  reason?: string;
};

export type LogEmitCommand = {
  type: 'log.emit';
  service: 'Frontend-app';
  level?: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  attributes?: Record<string, unknown>;
  timestamp?: string;
};
export type HoleInfoEvent = {
  type: 'hole.info';
  roundId?: string;
  courseId?: string;
  hole: number;
  par?: number;
  strokeIndex?: number;
  name?: string;
  description?: string;
  lengthMeters?: number;
};

export type TourLeaderboardStateEvent = {
  type: 'tour.leaderboard.state';
  tourId: string;
  generatedAt?: string;
  tour?: Record<string, unknown>;
  rounds?: Array<Record<string, unknown>>;
  games?: Array<Record<string, unknown>>;
  players?: Array<Record<string, unknown>>;
  roundMembersByRoundId?: Record<string, string[]>;
  scoresByTourGame?: Array<Record<string, unknown>>;
  scoresByRoundGame?: Array<Record<string, unknown>>;
  totals?: Array<Record<string, unknown>>;
  totalsByHole?: Record<string, unknown>;
};

export type TourLeaderboardUpdateEvent = {
  type: 'tour.leaderboard.update';
  tourId: string;
  generatedAt?: string;
  scoresByTourGame?: Array<Record<string, unknown>>;
  scoresByRoundGame?: Array<Record<string, unknown>>;
  totals?: Array<Record<string, unknown>>;
};

export type TourLeaderboardErrorEvent = {
  type: 'tour.leaderboard.error';
  tourId?: string;
  code?: string;
  message?: string;
  requestId?: string;
};

export type TourInviteEvent = {
  type: 'tour.invite';
  tourId?: string;
  inviteToken?: string;
  targetType?: 'tour' | 'round';
  roundId?: string;
  isHidden?: boolean;
  requestId?: string;
};

export type TourInviteErrorEvent = {
  type: 'tour.invite.error';
  tourId?: string;
  code?: string;
  message?: string;
  requestId?: string;
};

export type CoachMarkStateItem = {
  key: string;
  shownCount: number;
  maxShows: number;
  shouldShow: boolean;
};

export type CoachMarkStateEvent = {
  type: 'coachMark.state';
  requestId?: string;
  items?: CoachMarkStateItem[];
};

export type CoachMarkShownAckEvent = {
  type: 'coachMark.shown.ack';
  requestId?: string;
  key: string;
  shownCount: number;
  maxShows: number;
  shouldShow: boolean;
};

export type CoachMarkResetAckEvent = {
  type: 'coachMark.reset.ack';
  requestId?: string;
};

export type CoachMarkErrorEvent = {
  type: 'coachMark.error';
  requestId?: string;
  code?: string;
  message?: string;
};

const DEFAULT_COACH_MARK_MAX_SHOWS = 4;

export type BffInboundMessage =
  | HoleUpdateEvent
  | HoleUpdatedEvent
  | RoundStateEvent
  | PlayersSnapshotEvent
  | PlayersErrorEvent
  | TourUpdateEvent
  | TourCreatedEvent
  | TourInitiateRejectedEvent
  | TourMembersSnapshotEvent
  | TourMemberAddedEvent
  | TourMemberRemovedEvent
  | TourMemberRemoveRejectedEvent
  | TourMemberClaimedEvent
  | TourMemberClaimRejectedEvent
  | TourMemberClaimReleasedEvent
  | FriendListSnapshotEvent
  | FriendAddedEvent
  | FriendAddRejectedEvent
  | FriendRemovedEvent
  | FriendRemoveRejectedEvent
  | FriendUpdatedEvent
  | FriendUpdateRejectedEvent
  | RoundMemberAddedEvent
  | RoundMemberAddRejectedEvent
  | RoundMemberRemovedEvent
  | RoundMemberRemoveRejectedEvent
  | RoundRemovedEvent
  | RoundRemoveRejectedEvent
  | PlayerTemporaryCreatedEvent
  | RoundInitiateRejectedEvent
  | RoundCreatedEvent
  | RoundUpdatedEvent
  | RoundGroupsUpdatedEvent
  | RoundGroupsUpdateRejectedEvent
  | RoundGroupsSwapRejectedEvent
  | GameResultsEvent
  | TeamCreatedEvent
  | RoundLeaderboardEvent
  | HoleInfoEvent
  | TourLeaderboardStateEvent
  | TourLeaderboardUpdateEvent
  | TourLeaderboardErrorEvent
  | TourInviteEvent
  | TourInviteErrorEvent
  | CoachMarkStateEvent
  | CoachMarkShownAckEvent
  | CoachMarkResetAckEvent
  | CoachMarkErrorEvent
  | BugImagePresignAck
  | BugImagePresignError
  | BugReportAck
  | BugReportError
  | UserCreatedEvent
  | UserInfoEvent
  | GolfCourseChangedEvent
  | GolfCourseUpsertRejectedEvent
  | { type?: string; message?: string; [key: string]: unknown };

type MessageHandler = (message: BffInboundMessage) => void;
type ConnectionHandler = (state: 'connected' | 'disconnected') => void;
type ErrorHandler = (message: string) => void;

const toWebSocketUrl = (httpUrl: string) => {
  if (httpUrl.startsWith('wss://') || httpUrl.startsWith('ws://')) return httpUrl;
  if (httpUrl.startsWith('https://')) {
    return httpUrl.replace('https://', 'wss://');
  }
  return httpUrl.replace('http://', 'ws://');
};

const TOKEN_EXP_SKEW_SECONDS = 5;
const TOKEN_IAT_SKEW_SECONDS = 1;

type TokenMetadata = { exp: number | null; iat: number | null; sub: string | null };

const decodeBase64Url = (value: string): string | null => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
  if (typeof atob === 'function') {
    return atob(padded);
  }
  const buffer = (globalThis as { Buffer?: { from: (value: string, encoding: string) => { toString: (encoding: string) => string } } }).Buffer;
  if (buffer?.from) {
    return buffer.from(padded, 'base64').toString('utf-8');
  }
  return null;
};

const readTokenMetadata = (token: string): TokenMetadata => {
  const parts = token.split('.');
  if (parts.length < 2) {
    return { exp: null, iat: null, sub: null };
  }
  const payloadText = decodeBase64Url(parts[1]);
  if (!payloadText) {
    return { exp: null, iat: null, sub: null };
  }
  try {
    const payload = JSON.parse(payloadText) as { exp?: number; iat?: number; sub?: string };
    return {
      exp: typeof payload.exp === 'number' ? payload.exp : null,
      iat: typeof payload.iat === 'number' ? payload.iat : null,
      sub: typeof payload.sub === 'string' ? payload.sub : null,
    };
  } catch {
    return { exp: null, iat: null, sub: null };
  }
};

const isLikelyStaleTokenUpdate = (currentToken: string, nextToken: string) => {
  if (currentToken === nextToken) {
    return {
      stale: false,
      currentExp: null as number | null,
      nextExp: null as number | null,
      currentIat: null as number | null,
      nextIat: null as number | null,
    };
  }
  const currentMeta = readTokenMetadata(currentToken);
  const nextMeta = readTokenMetadata(nextToken);
  if (currentMeta.sub && nextMeta.sub && currentMeta.sub !== nextMeta.sub) {
    return {
      stale: false,
      currentExp: currentMeta.exp,
      nextExp: nextMeta.exp,
      currentIat: currentMeta.iat,
      nextIat: nextMeta.iat,
    };
  }
  if (currentMeta.iat !== null && nextMeta.iat !== null) {
    return {
      stale: nextMeta.iat + TOKEN_IAT_SKEW_SECONDS < currentMeta.iat,
      currentExp: currentMeta.exp,
      nextExp: nextMeta.exp,
      currentIat: currentMeta.iat,
      nextIat: nextMeta.iat,
    };
  }
  if (currentMeta.exp === null || nextMeta.exp === null) {
    return {
      stale: false,
      currentExp: currentMeta.exp,
      nextExp: nextMeta.exp,
      currentIat: currentMeta.iat,
      nextIat: nextMeta.iat,
    };
  }
  const nowSeconds = Date.now() / 1000;
  if (currentMeta.exp <= nowSeconds + TOKEN_EXP_SKEW_SECONDS) {
    return {
      stale: false,
      currentExp: currentMeta.exp,
      nextExp: nextMeta.exp,
      currentIat: currentMeta.iat,
      nextIat: nextMeta.iat,
    };
  }
  return {
    stale: nextMeta.exp + TOKEN_EXP_SKEW_SECONDS < currentMeta.exp,
    currentExp: currentMeta.exp,
    nextExp: nextMeta.exp,
    currentIat: currentMeta.iat,
    nextIat: nextMeta.iat,
  };
};

const normalizeMessage = (raw: Record<string, unknown>): BffInboundMessage => {
  if (!raw || typeof raw !== 'object') return raw as BffInboundMessage;
  const type = (raw as { type?: string; Type?: string }).type ?? (raw as { Type?: string }).Type;

  if (type === 'game.result') {
    const games = (raw as { games?: unknown; Games?: unknown }).games ?? (raw as { Games?: unknown }).Games;
    const normalizedGames = Array.isArray(games)
      ? games
          .filter((game) => game && typeof game === 'object')
          .map((game) => {
            const players = (game as { players?: unknown; Players?: unknown }).players ?? (game as { Players?: unknown }).Players;
            return {
              gameId: (game as { gameId?: string; GameId?: string }).gameId ?? (game as { GameId?: string }).GameId ?? '',
              gameName: (game as { gameName?: string; GameName?: string }).gameName ?? (game as { GameName?: string }).GameName,
              players: Array.isArray(players)
                ? players
                    .filter((player) => player && typeof player === 'object')
                    .map((player) => ({
                      playerId:
                        (player as { playerId?: string; PlayerId?: string }).playerId ??
                        (player as { PlayerId?: string }).PlayerId ??
                        '',
                      lastScorePoints:
                        (player as { lastScorePoints?: number; LastScorePoints?: number }).lastScorePoints ??
                        (player as { LastScorePoints?: number }).LastScorePoints ??
                        0,
                      holeScorePoints:
                        (player as { holeScorePoints?: Array<Record<string, unknown>>; HoleScorePoints?: Array<Record<string, unknown>> })
                          .holeScorePoints ??
                        (player as { HoleScorePoints?: Array<Record<string, unknown>> }).HoleScorePoints,
                      lastHoleNumber:
                        (player as { lastHoleNumber?: number; LastHoleNumber?: number }).lastHoleNumber ??
                        (player as { LastHoleNumber?: number }).LastHoleNumber,
                      lastGrossStrokes:
                        (player as { lastGrossStrokes?: number; LastGrossStrokes?: number }).lastGrossStrokes ??
                        (player as { LastGrossStrokes?: number }).LastGrossStrokes,
                    }))
                    .map((player) => ({
                      ...player,
                      holeScorePoints: player.holeScorePoints?.map((entry) => ({
                        holeNumber:
                          (entry as { holeNumber?: number }).holeNumber ??
                          (entry as { HoleNumber?: number }).HoleNumber ??
                          0,
                        scorePoints:
                          (entry as { scorePoints?: number }).scorePoints ??
                          (entry as { ScorePoints?: number }).ScorePoints ??
                          0,
                      })),
                    }))
                : [],
            } as GameResultGame;
          })
      : [];
    return {
      type: 'game.result',
      roundId: (raw as { roundId?: string; RoundId?: string }).roundId ?? (raw as { RoundId?: string }).RoundId ?? '',
      games: normalizedGames,
      updatedPlayerId:
        (raw as { updatedPlayerId?: string; UpdatedPlayerId?: string }).updatedPlayerId ??
        (raw as { UpdatedPlayerId?: string }).UpdatedPlayerId,
      updatedHoleNumber:
        (raw as { updatedHoleNumber?: number; UpdatedHoleNumber?: number }).updatedHoleNumber ??
        (raw as { UpdatedHoleNumber?: number }).UpdatedHoleNumber,
      calculatedAt:
        (raw as { calculatedAt?: string; CalculatedAt?: string }).calculatedAt ??
        (raw as { CalculatedAt?: string }).CalculatedAt,
    } as GameResultsEvent;
  }

  if (type === 'team.created') {
    return {
      type: 'team.created',
      teamId: (raw as { teamId?: string; TeamId?: string }).teamId ?? (raw as { TeamId?: string }).TeamId,
      requestId: (raw as { requestId?: string; RequestId?: string }).requestId ?? (raw as { RequestId?: string }).RequestId,
    };
  }

  if (type === 'round.groups.updated') {
    const rawGroups =
      (raw as { groups?: unknown[]; Groups?: unknown[] }).groups ??
      (raw as { Groups?: unknown[] }).Groups;
    const groups = Array.isArray(rawGroups)
      ? rawGroups.map((g: Record<string, unknown>) => {
          const gid = (g as { groupId?: string; GroupId?: string }).groupId ?? (g as { GroupId?: string }).GroupId;
          const rawTeams = (g as { teams?: unknown[]; Teams?: unknown[] }).teams ?? (g as { Teams?: unknown[] }).Teams;
          const teams = Array.isArray(rawTeams)
            ? rawTeams.map((t: Record<string, unknown>) => ({
                teamId: (t as { teamId?: string; TeamId?: string }).teamId ?? (t as { TeamId?: string }).TeamId,
                playerIds:
                  (t as { playerIds?: string[]; PlayerIds?: string[] }).playerIds ??
                  (t as { PlayerIds?: string[] }).PlayerIds,
              }))
            : undefined;
          return { groupId: gid, teams };
        })
      : undefined;
    return {
      type: 'round.groups.updated',
      requestId: (raw as { requestId?: string; RequestId?: string }).requestId ?? (raw as { RequestId?: string }).RequestId,
      roundId: (raw as { roundId?: string; RoundId?: string }).roundId ?? (raw as { RoundId?: string }).RoundId,
      groups,
      playerOrder:
        (raw as { playerOrder?: string[]; PlayerOrder?: string[] }).playerOrder ??
        (raw as { PlayerOrder?: string[] }).PlayerOrder,
      updatedAt: (raw as { updatedAt?: string; UpdatedAt?: string }).updatedAt ?? (raw as { UpdatedAt?: string }).UpdatedAt,
    } as RoundGroupsUpdatedEvent;
  }

  if (type === 'round.groups.update.rejected') {
    return {
      type: 'round.groups.update.rejected',
      requestId: (raw as { requestId?: string; RequestId?: string }).requestId ?? (raw as { RequestId?: string }).RequestId,
      roundId: (raw as { roundId?: string; RoundId?: string }).roundId ?? (raw as { RoundId?: string }).RoundId,
      code: (raw as { code?: string; Code?: string }).code ?? (raw as { Code?: string }).Code,
      messageKey:
        (raw as { messageKey?: string; MessageKey?: string }).messageKey ??
        (raw as { MessageKey?: string }).MessageKey,
      message: (raw as { message?: string; Message?: string }).message ?? (raw as { Message?: string }).Message,
      rejectedAt:
        (raw as { rejectedAt?: string; RejectedAt?: string }).rejectedAt ??
        (raw as { RejectedAt?: string }).RejectedAt,
    } as RoundGroupsUpdateRejectedEvent;
  }

  if (type === 'round.groups.swap.rejected') {
    return {
      type: 'round.groups.swap.rejected',
      requestId: (raw as { requestId?: string; RequestId?: string }).requestId ?? (raw as { RequestId?: string }).RequestId,
      roundId: (raw as { roundId?: string; RoundId?: string }).roundId ?? (raw as { RoundId?: string }).RoundId,
      code: (raw as { code?: string; Code?: string }).code ?? (raw as { Code?: string }).Code,
      messageKey:
        (raw as { messageKey?: string; MessageKey?: string }).messageKey ??
        (raw as { MessageKey?: string }).MessageKey,
      message: (raw as { message?: string; Message?: string }).message ?? (raw as { Message?: string }).Message,
      rejectedAt:
        (raw as { rejectedAt?: string; RejectedAt?: string }).rejectedAt ??
        (raw as { RejectedAt?: string }).RejectedAt,
    } as RoundGroupsSwapRejectedEvent;
  }

  if (type === 'round.leaderboard') {
    const totals = ((raw as { totals?: unknown; Totals?: unknown }).totals ??
      (raw as { Totals?: unknown }).Totals) as Array<Record<string, unknown>> | undefined;
    return {
      type: 'round.leaderboard',
      roundId: (raw as { roundId?: string; RoundId?: string }).roundId ?? (raw as { RoundId?: string }).RoundId ?? '',
      totals:
        totals?.map((t) => ({
          playerId:
            (t as { playerId?: string }).playerId ??
            (t as { PlayerId?: string }).PlayerId ??
            (t as { player?: string }).player ??
            (t as { Player?: string }).Player ??
            undefined,
          totalRoundGamePoints:
            (t as { totalRoundGamePoints?: number }).totalRoundGamePoints ??
            (t as { TotalRoundGamePoints?: number }).TotalRoundGamePoints ??
            0,
          tourGamesPoints:
            (t as { tourGamesPoints?: number }).tourGamesPoints ??
            (t as { TourGamesPoints?: number }).TourGamesPoints ??
            0,
          roundGamePoints:
            ((t as { roundGamePoints?: unknown }).roundGamePoints ??
              (t as { RoundGamePoints?: unknown }).RoundGamePoints) as Array<Record<string, unknown>> | undefined,
        }))
          ?.map((entry) => ({
            ...entry,
            roundGamePoints: entry.roundGamePoints?.map((gp) => ({
              gameId: (gp as { gameId?: string }).gameId ?? (gp as { GameId?: string }).GameId,
              roundGamePoints:
                (gp as { roundGamePoints?: number }).roundGamePoints ??
                (gp as { RoundGamePoints?: number }).RoundGamePoints ??
                0,
              position: (gp as { position?: number }).position ?? (gp as { Position?: number }).Position,
            })),
          })) ?? [],
    };
  }

  if (type === 'hole.update' || type === 'hole.updated') {
    return {
      ...raw,
      type: 'hole.update',
      roundId: (raw as { roundId?: string; RoundId?: string }).roundId ?? (raw as { RoundId?: string }).RoundId ?? '',
      tourId: (raw as { tourId?: string; TourId?: string }).tourId ?? (raw as { TourId?: string }).TourId,
    } as HoleUpdateEvent;
  }

  if (type === 'hole.info') {
    return {
      ...raw,
      type: 'hole.info',
      roundId: (raw as { roundId?: string; RoundId?: string }).roundId ?? (raw as { RoundId?: string }).RoundId,
      courseId: (raw as { courseId?: string; CourseId?: string }).courseId ?? (raw as { CourseId?: string }).CourseId,
      hole: (raw as { hole?: number; Hole?: number }).hole ?? (raw as { Hole?: number }).Hole ?? 0,
      par: (raw as { par?: number; Par?: number }).par ?? (raw as { Par?: number }).Par,
      strokeIndex: (raw as { strokeIndex?: number; StrokeIndex?: number }).strokeIndex ?? (raw as { StrokeIndex?: number }).StrokeIndex,
      lengthMeters: (raw as { lengthMeters?: number; LengthMeters?: number }).lengthMeters ?? (raw as { LengthMeters?: number }).LengthMeters,
      name: (raw as { name?: string; Name?: string }).name ?? (raw as { Name?: string }).Name,
      description: (raw as { description?: string; Description?: string }).description ?? (raw as { Description?: string }).Description,
    } as HoleInfoEvent;
  }

  if (type === 'round.state') {
    return {
      ...raw,
      type: 'round.state',
      roundId: (raw as { roundId?: string; RoundId?: string }).roundId ?? (raw as { RoundId?: string }).RoundId ?? '',
    } as RoundStateEvent;
  }

  if (type === 'tour.leaderboard.state') {
    return {
      type: 'tour.leaderboard.state',
      tourId: (raw as { tourId?: string; TourId?: string }).tourId ?? (raw as { TourId?: string }).TourId ?? '',
      generatedAt:
        (raw as { generatedAt?: string; GeneratedAt?: string }).generatedAt ??
        (raw as { GeneratedAt?: string }).GeneratedAt,
      tour:
        ((raw as { tour?: Record<string, unknown>; Tour?: Record<string, unknown> }).tour ??
          (raw as { Tour?: Record<string, unknown> }).Tour) ??
        {},
      rounds:
        ((raw as { rounds?: Array<Record<string, unknown>>; Rounds?: Array<Record<string, unknown>> }).rounds ??
          (raw as { Rounds?: Array<Record<string, unknown>> }).Rounds) ??
        [],
      games:
        ((raw as { games?: Array<Record<string, unknown>>; Games?: Array<Record<string, unknown>> }).games ??
          (raw as { Games?: Array<Record<string, unknown>> }).Games) ??
        [],
      players:
        ((raw as { players?: Array<Record<string, unknown>>; Players?: Array<Record<string, unknown>> }).players ??
          (raw as { Players?: Array<Record<string, unknown>> }).Players) ??
        [],
      roundMembersByRoundId:
        ((raw as {
          roundMembersByRoundId?: Record<string, unknown>;
          RoundMembersByRoundId?: Record<string, unknown>;
        }).roundMembersByRoundId ??
          (raw as { RoundMembersByRoundId?: Record<string, unknown> }).RoundMembersByRoundId ??
          {}) as Record<string, string[]>,
      scoresByTourGame:
        ((raw as {
          scoresByTourGame?: Array<Record<string, unknown>>;
          ScoresByTourGame?: Array<Record<string, unknown>>;
        }).scoresByTourGame ??
          (raw as { ScoresByTourGame?: Array<Record<string, unknown>> }).ScoresByTourGame) ??
        [],
      scoresByRoundGame:
        ((raw as {
          scoresByRoundGame?: Array<Record<string, unknown>>;
          ScoresByRoundGame?: Array<Record<string, unknown>>;
        }).scoresByRoundGame ??
          (raw as { ScoresByRoundGame?: Array<Record<string, unknown>> }).ScoresByRoundGame) ??
        [],
      totals:
        ((raw as { totals?: Array<Record<string, unknown>>; Totals?: Array<Record<string, unknown>> }).totals ??
          (raw as { Totals?: Array<Record<string, unknown>> }).Totals) ??
        [],
      totalsByHole:
        ((raw as { totalsByHole?: Record<string, unknown>; TotalsByHole?: Record<string, unknown> }).totalsByHole ??
          (raw as { TotalsByHole?: Record<string, unknown> }).TotalsByHole) ??
        undefined,
    } as TourLeaderboardStateEvent;
  }

  if (type === 'tour.leaderboard.update') {
    return {
      type: 'tour.leaderboard.update',
      tourId: (raw as { tourId?: string; TourId?: string }).tourId ?? (raw as { TourId?: string }).TourId ?? '',
      generatedAt:
        (raw as { generatedAt?: string; GeneratedAt?: string }).generatedAt ??
        (raw as { GeneratedAt?: string }).GeneratedAt,
      scoresByTourGame:
        ((raw as {
          scoresByTourGame?: Array<Record<string, unknown>>;
          ScoresByTourGame?: Array<Record<string, unknown>>;
        }).scoresByTourGame ??
          (raw as { ScoresByTourGame?: Array<Record<string, unknown>> }).ScoresByTourGame) ??
        [],
      scoresByRoundGame:
        ((raw as {
          scoresByRoundGame?: Array<Record<string, unknown>>;
          ScoresByRoundGame?: Array<Record<string, unknown>>;
        }).scoresByRoundGame ??
          (raw as { ScoresByRoundGame?: Array<Record<string, unknown>> }).ScoresByRoundGame) ??
        [],
      totals:
        ((raw as { totals?: Array<Record<string, unknown>>; Totals?: Array<Record<string, unknown>> }).totals ??
          (raw as { Totals?: Array<Record<string, unknown>> }).Totals) ??
        [],
    } as TourLeaderboardUpdateEvent;
  }

  if (type === 'tour.leaderboard.error') {
    return {
      type: 'tour.leaderboard.error',
      tourId: (raw as { tourId?: string; TourId?: string }).tourId ?? (raw as { TourId?: string }).TourId,
      code: (raw as { code?: string; Code?: string }).code ?? (raw as { Code?: string }).Code,
      message: (raw as { message?: string; Message?: string }).message ?? (raw as { Message?: string }).Message,
      requestId:
        (raw as { requestId?: string; RequestId?: string }).requestId ??
        (raw as { RequestId?: string }).RequestId,
    } as TourLeaderboardErrorEvent;
  }

  if (type === 'tour.invite') {
    return {
      type: 'tour.invite',
      tourId: (raw as { tourId?: string; TourId?: string }).tourId ?? (raw as { TourId?: string }).TourId,
      inviteToken:
        (raw as { inviteToken?: string; InviteToken?: string }).inviteToken ??
        (raw as { InviteToken?: string }).InviteToken,
      targetType:
        (raw as { targetType?: 'tour' | 'round'; TargetType?: 'tour' | 'round' }).targetType ??
        (raw as { TargetType?: 'tour' | 'round' }).TargetType,
      roundId:
        (raw as { roundId?: string; RoundId?: string }).roundId ??
        (raw as { RoundId?: string }).RoundId,
      isHidden:
        (raw as { isHidden?: boolean; IsHidden?: boolean }).isHidden ??
        (raw as { IsHidden?: boolean }).IsHidden,
      requestId:
        (raw as { requestId?: string; RequestId?: string }).requestId ??
        (raw as { RequestId?: string }).RequestId,
    } as TourInviteEvent;
  }

  if (type === 'tour.invite.error') {
    return {
      type: 'tour.invite.error',
      tourId: (raw as { tourId?: string; TourId?: string }).tourId ?? (raw as { TourId?: string }).TourId,
      code: (raw as { code?: string; Code?: string }).code ?? (raw as { Code?: string }).Code,
      message: (raw as { message?: string; Message?: string }).message ?? (raw as { Message?: string }).Message,
      requestId:
        (raw as { requestId?: string; RequestId?: string }).requestId ??
        (raw as { RequestId?: string }).RequestId,
    } as TourInviteErrorEvent;
  }

  if (type === 'tour.members.snapshot') {
    const membersRaw =
      (raw as { members?: unknown; Members?: unknown }).members ??
      (raw as { Members?: unknown }).Members;
    const platformPlayersRaw =
      (raw as { platformPlayers?: unknown; PlatformPlayers?: unknown }).platformPlayers ??
      (raw as { PlatformPlayers?: unknown }).PlatformPlayers;

    return {
      type: 'tour.members.snapshot',
      requestId:
        (raw as { requestId?: string; RequestId?: string }).requestId ??
        (raw as { RequestId?: string }).RequestId,
      tourId:
        (raw as { tourId?: string; TourId?: string }).tourId ??
        (raw as { TourId?: string }).TourId,
      members: Array.isArray(membersRaw)
        ? membersRaw
            .filter((member) => member && typeof member === 'object')
            .map((member) => ({
              playerId:
                (member as { playerId?: string; PlayerId?: string }).playerId ??
                (member as { PlayerId?: string }).PlayerId ??
                '',
              name:
                (member as { name?: string; Name?: string }).name ??
                (member as { Name?: string }).Name ??
                '',
              userId:
                (member as { userId?: string | null; UserId?: string | null }).userId ??
                (member as { UserId?: string | null }).UserId ??
                null,
              isTemporary:
                (member as { isTemporary?: boolean; IsTemporary?: boolean }).isTemporary ??
                (member as { IsTemporary?: boolean }).IsTemporary,
              joinedAt:
                (member as { joinedAt?: string; JoinedAt?: string }).joinedAt ??
                (member as { JoinedAt?: string }).JoinedAt,
            }))
        : [],
      platformPlayers: Array.isArray(platformPlayersRaw)
        ? platformPlayersRaw
            .filter((player) => player && typeof player === 'object')
            .map((player) => ({
              playerId:
                (player as { playerId?: string; PlayerId?: string }).playerId ??
                (player as { PlayerId?: string }).PlayerId ??
                '',
              name:
                (player as { name?: string; Name?: string }).name ??
                (player as { Name?: string }).Name ??
                '',
              userId:
                (player as { userId?: string | null; UserId?: string | null }).userId ??
                (player as { UserId?: string | null }).UserId ??
                null,
              isTemporary:
                (player as { isTemporary?: boolean; IsTemporary?: boolean }).isTemporary ??
                (player as { IsTemporary?: boolean }).IsTemporary,
              createdAt:
                (player as { createdAt?: string; CreatedAt?: string }).createdAt ??
                (player as { CreatedAt?: string }).CreatedAt,
            }))
        : [],
    } as TourMembersSnapshotEvent;
  }

  if (type === 'tour.member.added') {
    const memberRaw =
      (raw as { member?: unknown; Member?: unknown }).member ??
      (raw as { Member?: unknown }).Member;
    const normalizedMember =
      memberRaw && typeof memberRaw === 'object'
        ? {
            playerId:
              (memberRaw as { playerId?: string; PlayerId?: string }).playerId ??
              (memberRaw as { PlayerId?: string }).PlayerId ??
              '',
            name:
              (memberRaw as { name?: string; Name?: string }).name ??
              (memberRaw as { Name?: string }).Name ??
              '',
            userId:
              (memberRaw as { userId?: string | null; UserId?: string | null }).userId ??
              (memberRaw as { UserId?: string | null }).UserId ??
              null,
            isTemporary:
              (memberRaw as { isTemporary?: boolean; IsTemporary?: boolean }).isTemporary ??
              (memberRaw as { IsTemporary?: boolean }).IsTemporary,
            joinedAt:
              (memberRaw as { joinedAt?: string; JoinedAt?: string }).joinedAt ??
              (memberRaw as { JoinedAt?: string }).JoinedAt,
          }
        : undefined;
    return {
      type: 'tour.member.added',
      requestId:
        (raw as { requestId?: string; RequestId?: string }).requestId ??
        (raw as { RequestId?: string }).RequestId,
      tourId:
        (raw as { tourId?: string; TourId?: string }).tourId ??
        (raw as { TourId?: string }).TourId,
      member: normalizedMember,
    } as TourMemberAddedEvent;
  }

  if (type === 'tour.member.removed') {
    const removedFromRoundIdsRaw =
      (raw as { removedFromRoundIds?: unknown; RemovedFromRoundIds?: unknown }).removedFromRoundIds ??
      (raw as { RemovedFromRoundIds?: unknown }).RemovedFromRoundIds;
    return {
      type: 'tour.member.removed',
      requestId:
        (raw as { requestId?: string; RequestId?: string }).requestId ??
        (raw as { RequestId?: string }).RequestId,
      tourId:
        (raw as { tourId?: string; TourId?: string }).tourId ??
        (raw as { TourId?: string }).TourId,
      playerId:
        (raw as { playerId?: string; PlayerId?: string }).playerId ??
        (raw as { PlayerId?: string }).PlayerId,
      removedFromRoundIds: Array.isArray(removedFromRoundIdsRaw)
        ? removedFromRoundIdsRaw.filter((value): value is string => typeof value === 'string')
        : undefined,
      removedAt:
        (raw as { removedAt?: string; RemovedAt?: string }).removedAt ??
        (raw as { RemovedAt?: string }).RemovedAt,
    } as TourMemberRemovedEvent;
  }

  if (type === 'tour.member.remove.rejected') {
    return {
      type: 'tour.member.remove.rejected',
      requestId:
        (raw as { requestId?: string; RequestId?: string }).requestId ??
        (raw as { RequestId?: string }).RequestId,
      tourId:
        (raw as { tourId?: string; TourId?: string }).tourId ??
        (raw as { TourId?: string }).TourId,
      playerId:
        (raw as { playerId?: string; PlayerId?: string }).playerId ??
        (raw as { PlayerId?: string }).PlayerId,
      code:
        (raw as { code?: string; Code?: string }).code ??
        (raw as { Code?: string }).Code,
      messageKey:
        (raw as { messageKey?: string; MessageKey?: string }).messageKey ??
        (raw as { MessageKey?: string }).MessageKey,
      message:
        (raw as { message?: string; Message?: string }).message ??
        (raw as { Message?: string }).Message,
      rejectedAt:
        (raw as { rejectedAt?: string; RejectedAt?: string }).rejectedAt ??
        (raw as { RejectedAt?: string }).RejectedAt,
    } as TourMemberRemoveRejectedEvent;
  }

  if (type === 'tour.member.claimed') {
    return {
      type: 'tour.member.claimed',
      requestId:
        (raw as { requestId?: string; RequestId?: string }).requestId ??
        (raw as { RequestId?: string }).RequestId,
      tourId:
        (raw as { tourId?: string; TourId?: string }).tourId ??
        (raw as { TourId?: string }).TourId,
      sourcePlayerId:
        (raw as { sourcePlayerId?: string; SourcePlayerId?: string }).sourcePlayerId ??
        (raw as { SourcePlayerId?: string }).SourcePlayerId,
      claimClientId:
        (raw as { claimClientId?: string; ClaimClientId?: string }).claimClientId ??
        (raw as { ClaimClientId?: string }).ClaimClientId,
      startedRoundId:
        (raw as { startedRoundId?: string; StartedRoundId?: string }).startedRoundId ??
        (raw as { StartedRoundId?: string }).StartedRoundId,
      claimedAt:
        (raw as { claimedAt?: string; ClaimedAt?: string }).claimedAt ??
        (raw as { ClaimedAt?: string }).ClaimedAt,
      claimToken:
        (raw as { claimToken?: string; ClaimToken?: string }).claimToken ??
        (raw as { ClaimToken?: string }).ClaimToken,
    } as TourMemberClaimedEvent;
  }

  if (type === 'tour.member.claim.rejected') {
    return {
      type: 'tour.member.claim.rejected',
      requestId:
        (raw as { requestId?: string; RequestId?: string }).requestId ??
        (raw as { RequestId?: string }).RequestId,
      tourId:
        (raw as { tourId?: string; TourId?: string }).tourId ??
        (raw as { TourId?: string }).TourId,
      sourcePlayerId:
        (raw as { sourcePlayerId?: string; SourcePlayerId?: string }).sourcePlayerId ??
        (raw as { SourcePlayerId?: string }).SourcePlayerId,
      claimClientId:
        (raw as { claimClientId?: string; ClaimClientId?: string }).claimClientId ??
        (raw as { ClaimClientId?: string }).ClaimClientId,
      code:
        (raw as { code?: string; Code?: string }).code ??
        (raw as { Code?: string }).Code,
      messageKey:
        (raw as { messageKey?: string; MessageKey?: string }).messageKey ??
        (raw as { MessageKey?: string }).MessageKey,
      message:
        (raw as { message?: string; Message?: string }).message ??
        (raw as { Message?: string }).Message,
      rejectedAt:
        (raw as { rejectedAt?: string; RejectedAt?: string }).rejectedAt ??
        (raw as { RejectedAt?: string }).RejectedAt,
    } as TourMemberClaimRejectedEvent;
  }

  if (type === 'tour.member.claim.released') {
    return {
      type: 'tour.member.claim.released',
      requestId:
        (raw as { requestId?: string; RequestId?: string }).requestId ??
        (raw as { RequestId?: string }).RequestId,
      tourId:
        (raw as { tourId?: string; TourId?: string }).tourId ??
        (raw as { TourId?: string }).TourId,
      claimClientId:
        (raw as { claimClientId?: string; ClaimClientId?: string }).claimClientId ??
        (raw as { ClaimClientId?: string }).ClaimClientId,
      releasedAt:
        (raw as { releasedAt?: string; ReleasedAt?: string }).releasedAt ??
        (raw as { ReleasedAt?: string }).ReleasedAt,
    } as TourMemberClaimReleasedEvent;
  }

  if (type === 'player.temporary.created' || type === 'players.temporary.created') {
    return {
      type: 'player.temporary.created',
      requestId:
        (raw as { requestId?: string; RequestId?: string }).requestId ??
        (raw as { RequestId?: string }).RequestId,
      playerId:
        (raw as { playerId?: string; PlayerId?: string }).playerId ??
        (raw as { PlayerId?: string }).PlayerId,
      name:
        (raw as { name?: string; Name?: string }).name ??
        (raw as { Name?: string }).Name,
      isTemporary:
        (raw as { isTemporary?: boolean; IsTemporary?: boolean }).isTemporary ??
        (raw as { IsTemporary?: boolean }).IsTemporary,
      createdAt:
        (raw as { createdAt?: string; CreatedAt?: string }).createdAt ??
        (raw as { CreatedAt?: string }).CreatedAt,
    } as PlayerTemporaryCreatedEvent;
  }

  if (type === 'friend.list.snapshot') {
    const friendsRaw =
      (raw as { friends?: unknown; Friends?: unknown }).friends ??
      (raw as { Friends?: unknown }).Friends;
    return {
      type: 'friend.list.snapshot',
      requestId:
        (raw as { requestId?: string; RequestId?: string }).requestId ??
        (raw as { RequestId?: string }).RequestId,
      ownerUserId:
        (raw as { ownerUserId?: string; OwnerUserId?: string }).ownerUserId ??
        (raw as { OwnerUserId?: string }).OwnerUserId,
      friends: Array.isArray(friendsRaw)
        ? friendsRaw
            .filter((entry) => entry && typeof entry === 'object')
            .map((entry) => ({
              friendId:
                (entry as { friendId?: string; FriendId?: string }).friendId ??
                (entry as { FriendId?: string }).FriendId ??
                '',
              playerId:
                (entry as { playerId?: string; PlayerId?: string }).playerId ??
                (entry as { PlayerId?: string }).PlayerId ??
                '',
              nickname:
                (entry as { nickname?: string | null; Nickname?: string | null }).nickname ??
                (entry as { Nickname?: string | null }).Nickname ??
                null,
              name:
                (entry as { name?: string; Name?: string }).name ??
                (entry as { Name?: string }).Name ??
                '',
              playerName:
                (entry as { playerName?: string | null; PlayerName?: string | null }).playerName ??
                (entry as { PlayerName?: string | null }).PlayerName ??
                null,
              userId:
                (entry as { userId?: string | null; UserId?: string | null }).userId ??
                (entry as { UserId?: string | null }).UserId ??
                null,
              status:
                (entry as { status?: 'linked' | 'guest'; Status?: 'linked' | 'guest' }).status ??
                (entry as { Status?: 'linked' | 'guest' }).Status,
              createdAt:
                (entry as { createdAt?: string; CreatedAt?: string }).createdAt ??
                (entry as { CreatedAt?: string }).CreatedAt,
              archivedAt:
                (entry as { archivedAt?: string | null; ArchivedAt?: string | null }).archivedAt ??
                (entry as { ArchivedAt?: string | null }).ArchivedAt ??
                null,
            }))
        : [],
    } as FriendListSnapshotEvent;
  }

  if (type === 'user.info') {
    return {
      type: 'user.info',
      userId: (raw as { userId?: string; UserId?: string }).userId ?? (raw as { UserId?: string }).UserId,
      forename: (raw as { forename?: string; Forename?: string }).forename ?? (raw as { Forename?: string }).Forename,
      createdAt: (raw as { createdAt?: string; CreatedAt?: string }).createdAt ?? (raw as { CreatedAt?: string }).CreatedAt,
      requestId: (raw as { requestId?: string; RequestId?: string }).requestId ?? (raw as { RequestId?: string }).RequestId,
      success: (raw as { success?: boolean; Success?: boolean }).success ?? (raw as { Success?: boolean }).Success,
      error: (raw as { error?: string; Error?: string }).error ?? (raw as { Error?: string }).Error,
    } as UserInfoEvent;
  }

  if (type === 'round.created') {
    const playDateRaw =
      (raw as { playDate?: string; PlayDate?: string }).playDate ??
      (raw as { PlayDate?: string }).PlayDate;
    return {
      type: 'round.created',
      requestId:
        (raw as { requestId?: string; RequestId?: string }).requestId ??
        (raw as { RequestId?: string }).RequestId,
      roundId:
        (raw as { roundId?: string; RoundId?: string }).roundId ??
        (raw as { RoundId?: string }).RoundId,
      tourId:
        (raw as { tourId?: string; TourId?: string }).tourId ??
        (raw as { TourId?: string }).TourId,
      gameDefinitionIds:
        (raw as { gameDefinitionIds?: string[]; GameDefinitionIds?: string[] }).gameDefinitionIds ??
        (raw as { GameDefinitionIds?: string[] }).GameDefinitionIds,
      course:
        ((raw as {
          course?: { id?: string; name?: string; address?: string; lat?: number; lon?: number };
          Course?: { Id?: string; Name?: string; Address?: string; Lat?: number; Lon?: number };
        }).course ??
          (() => {
            const courseRaw = (raw as { Course?: Record<string, unknown> }).Course;
            if (!courseRaw) return undefined;
            return {
              id: (courseRaw.Id as string | undefined) ?? (courseRaw.id as string | undefined),
              name: (courseRaw.Name as string | undefined) ?? (courseRaw.name as string | undefined),
              address: (courseRaw.Address as string | undefined) ?? (courseRaw.address as string | undefined),
              lat: (courseRaw.Lat as number | undefined) ?? (courseRaw.lat as number | undefined),
              lon: (courseRaw.Lon as number | undefined) ?? (courseRaw.lon as number | undefined),
            };
          })()),
      holesCount:
        (raw as { holesCount?: number; HolesCount?: number }).holesCount ??
        (raw as { HolesCount?: number }).HolesCount,
      playDate: isLocalDateString(playDateRaw) ? playDateRaw : undefined,
      createdAt:
        (raw as { createdAt?: string; CreatedAt?: string }).createdAt ??
        (raw as { CreatedAt?: string }).CreatedAt,
    } as RoundCreatedEvent;
  }

  if (type === 'round.updated') {
    const playDateRaw =
      (raw as { playDate?: string; PlayDate?: string }).playDate ??
      (raw as { PlayDate?: string }).PlayDate;
    const gameDefinitionIdsRaw =
      (raw as { gameDefinitionIds?: unknown; GameDefinitionIds?: unknown }).gameDefinitionIds ??
      (raw as { GameDefinitionIds?: unknown }).GameDefinitionIds;
    const gameWeightsRaw =
      (raw as { gameWeights?: unknown; GameWeights?: unknown }).gameWeights ??
      (raw as { GameWeights?: unknown }).GameWeights;
    const courseRaw =
      (raw as { course?: unknown; Course?: unknown }).course ??
      (raw as { Course?: unknown }).Course;

    return {
      type: 'round.updated',
      roundId:
        (raw as { roundId?: string; RoundId?: string }).roundId ??
        (raw as { RoundId?: string }).RoundId,
      gameDefinitionIds: Array.isArray(gameDefinitionIdsRaw)
        ? gameDefinitionIdsRaw.filter((value): value is string => typeof value === 'string')
        : undefined,
      gameWeights: Array.isArray(gameWeightsRaw)
        ? gameWeightsRaw
            .filter((value) => value && typeof value === 'object')
            .map((value) => ({
              gameDefinitionId:
                (value as { gameDefinitionId?: string; GameDefinitionId?: string }).gameDefinitionId ??
                (value as { GameDefinitionId?: string }).GameDefinitionId,
              weightPercent:
                (value as { weightPercent?: number; WeightPercent?: number }).weightPercent ??
                (value as { WeightPercent?: number }).WeightPercent,
            }))
        : undefined,
      course:
        courseRaw && typeof courseRaw === 'object'
          ? {
              id:
                (courseRaw as { id?: string; Id?: string }).id ??
                (courseRaw as { Id?: string }).Id,
              name:
                (courseRaw as { name?: string; Name?: string }).name ??
                (courseRaw as { Name?: string }).Name,
              address:
                (courseRaw as { address?: string; Address?: string }).address ??
                (courseRaw as { Address?: string }).Address,
              lat:
                (courseRaw as { lat?: number; Lat?: number }).lat ??
                (courseRaw as { Lat?: number }).Lat,
              lon:
                (courseRaw as { lon?: number; Lon?: number }).lon ??
                (courseRaw as { Lon?: number }).Lon,
            }
          : undefined,
      holesCount:
        (raw as { holesCount?: number; HolesCount?: number }).holesCount ??
        (raw as { HolesCount?: number }).HolesCount,
      playDate: isLocalDateString(playDateRaw) ? playDateRaw : undefined,
      name: typeof (raw as { name?: unknown }).name === 'string' ? (raw as { name?: string }).name : undefined,
    } as RoundUpdatedEvent;
  }

  if (type === 'round.member.added') {
    return {
      type: 'round.member.added',
      requestId:
        (raw as { requestId?: string; RequestId?: string }).requestId ??
        (raw as { RequestId?: string }).RequestId,
      roundId:
        (raw as { roundId?: string; RoundId?: string }).roundId ??
        (raw as { RoundId?: string }).RoundId,
      tourId:
        (raw as { tourId?: string; TourId?: string }).tourId ??
        (raw as { TourId?: string }).TourId,
      playerId:
        (raw as { playerId?: string; PlayerId?: string }).playerId ??
        (raw as { PlayerId?: string }).PlayerId,
      addedAt:
        (raw as { addedAt?: string; AddedAt?: string }).addedAt ??
        (raw as { AddedAt?: string }).AddedAt,
    } as RoundMemberAddedEvent;
  }

  if (type === 'round.member.add.rejected') {
    return {
      type: 'round.member.add.rejected',
      requestId:
        (raw as { requestId?: string; RequestId?: string }).requestId ??
        (raw as { RequestId?: string }).RequestId,
      roundId:
        (raw as { roundId?: string; RoundId?: string }).roundId ??
        (raw as { RoundId?: string }).RoundId,
      tourId:
        (raw as { tourId?: string; TourId?: string }).tourId ??
        (raw as { TourId?: string }).TourId,
      playerId:
        (raw as { playerId?: string; PlayerId?: string }).playerId ??
        (raw as { PlayerId?: string }).PlayerId,
      code:
        (raw as { code?: string; Code?: string }).code ??
        (raw as { Code?: string }).Code,
      messageKey:
        (raw as { messageKey?: string; MessageKey?: string }).messageKey ??
        (raw as { MessageKey?: string }).MessageKey,
      message:
        (raw as { message?: string; Message?: string }).message ??
        (raw as { Message?: string }).Message,
      rejectedAt:
        (raw as { rejectedAt?: string; RejectedAt?: string }).rejectedAt ??
        (raw as { RejectedAt?: string }).RejectedAt,
    } as RoundMemberAddRejectedEvent;
  }

  if (type === 'round.member.removed') {
    return {
      type: 'round.member.removed',
      requestId:
        (raw as { requestId?: string; RequestId?: string }).requestId ??
        (raw as { RequestId?: string }).RequestId,
      roundId:
        (raw as { roundId?: string; RoundId?: string }).roundId ??
        (raw as { RoundId?: string }).RoundId,
      tourId:
        (raw as { tourId?: string; TourId?: string }).tourId ??
        (raw as { TourId?: string }).TourId,
      playerId:
        (raw as { playerId?: string; PlayerId?: string }).playerId ??
        (raw as { PlayerId?: string }).PlayerId,
      removedAt:
        (raw as { removedAt?: string; RemovedAt?: string }).removedAt ??
        (raw as { RemovedAt?: string }).RemovedAt,
    } as RoundMemberRemovedEvent;
  }

  if (type === 'round.member.remove.rejected') {
    return {
      type: 'round.member.remove.rejected',
      requestId:
        (raw as { requestId?: string; RequestId?: string }).requestId ??
        (raw as { RequestId?: string }).RequestId,
      roundId:
        (raw as { roundId?: string; RoundId?: string }).roundId ??
        (raw as { RoundId?: string }).RoundId,
      tourId:
        (raw as { tourId?: string; TourId?: string }).tourId ??
        (raw as { TourId?: string }).TourId,
      playerId:
        (raw as { playerId?: string; PlayerId?: string }).playerId ??
        (raw as { PlayerId?: string }).PlayerId,
      code:
        (raw as { code?: string; Code?: string }).code ??
        (raw as { Code?: string }).Code,
      messageKey:
        (raw as { messageKey?: string; MessageKey?: string }).messageKey ??
        (raw as { MessageKey?: string }).MessageKey,
      message:
        (raw as { message?: string; Message?: string }).message ??
        (raw as { Message?: string }).Message,
      rejectedAt:
        (raw as { rejectedAt?: string; RejectedAt?: string }).rejectedAt ??
        (raw as { RejectedAt?: string }).RejectedAt,
    } as RoundMemberRemoveRejectedEvent;
  }

  if (type === 'coachMark.state') {
    const itemsRaw =
      (raw as { items?: unknown; Items?: unknown }).items ??
      (raw as { Items?: unknown }).Items;
    return {
      type: 'coachMark.state',
      requestId:
        (raw as { requestId?: string; RequestId?: string }).requestId ??
        (raw as { RequestId?: string }).RequestId,
      items: Array.isArray(itemsRaw)
        ? itemsRaw
            .filter((entry) => entry && typeof entry === 'object')
            .map((entry) => ({
              key:
                (entry as { key?: string; Key?: string }).key ??
                (entry as { Key?: string }).Key ??
                '',
              shownCount:
                (entry as { shownCount?: number; ShownCount?: number }).shownCount ??
                (entry as { ShownCount?: number }).ShownCount ??
                0,
              maxShows:
                (entry as { maxShows?: number; MaxShows?: number }).maxShows ??
                (entry as { MaxShows?: number }).MaxShows ??
                DEFAULT_COACH_MARK_MAX_SHOWS,
              shouldShow:
                (entry as { shouldShow?: boolean; ShouldShow?: boolean }).shouldShow ??
                (entry as { ShouldShow?: boolean }).ShouldShow ??
                false,
            }))
        : [],
    } as CoachMarkStateEvent;
  }

  if (type === 'coachMark.shown.ack') {
    return {
      type: 'coachMark.shown.ack',
      requestId:
        (raw as { requestId?: string; RequestId?: string }).requestId ??
        (raw as { RequestId?: string }).RequestId,
      key:
        (raw as { key?: string; Key?: string }).key ??
        (raw as { Key?: string }).Key ??
        '',
      shownCount:
        (raw as { shownCount?: number; ShownCount?: number }).shownCount ??
        (raw as { ShownCount?: number }).ShownCount ??
        0,
      maxShows:
        (raw as { maxShows?: number; MaxShows?: number }).maxShows ??
        (raw as { MaxShows?: number }).MaxShows ??
        DEFAULT_COACH_MARK_MAX_SHOWS,
      shouldShow:
        (raw as { shouldShow?: boolean; ShouldShow?: boolean }).shouldShow ??
        (raw as { ShouldShow?: boolean }).ShouldShow ??
        false,
    } as CoachMarkShownAckEvent;
  }

  if (type === 'coachMark.reset.ack') {
    return {
      type: 'coachMark.reset.ack',
      requestId:
        (raw as { requestId?: string; RequestId?: string }).requestId ??
        (raw as { RequestId?: string }).RequestId,
    } as CoachMarkResetAckEvent;
  }

  if (type === 'coachMark.error') {
    return {
      type: 'coachMark.error',
      requestId:
        (raw as { requestId?: string; RequestId?: string }).requestId ??
        (raw as { RequestId?: string }).RequestId,
      code:
        (raw as { code?: string; Code?: string }).code ??
        (raw as { Code?: string }).Code,
      message:
        (raw as { message?: string; Message?: string }).message ??
        (raw as { Message?: string }).Message,
    } as CoachMarkErrorEvent;
  }

  return { ...raw, type: type as string } as BffInboundMessage;
};

export class BffWebSocketClient {
  private static instance: BffWebSocketClient | null = null;
  private socket: WebSocket | null = null;
  private socketGeneration = 0;
  private readonly baseHttpUrl: string;
  private readonly wsPath: string;
  private authToken: string | null = null;
  private claimAccessToken: string | null = null;
  private claimClientId: string | null = null;
  private inviteToken: string | null = null;
  private allowAnonymousReadOnly = false;
  private subscribedRoundId: string | null = null;
  private subscribedTourId: string | null = null;
  private suppressReconnect = false;
  private isOpen = false;
  private readonly messageHandlers = new Set<MessageHandler>();
  private readonly connectionHandlers = new Set<ConnectionHandler>();
  private readonly errorHandlers = new Set<ErrorHandler>();
  private readonly outboundQueue: Record<string, unknown>[] = [];
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private keepAliveTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly reconnectBaseDelayMs = 500;
  private readonly reconnectMaxDelayMs = 10000;
  private readonly connectTimeoutMs = 12000;
  private reconnectAttempt = 0;
  private updateBatchDepth = 0;
  private pendingReconnect = false;
  private readonly debugEnabled =
    process.env.EXPO_PUBLIC_BFF_WS_DEBUG === 'true' || process.env.EXPO_PUBLIC_BFF_WS_DEBUG === undefined;
  private readonly keepAliveIntervalMs = 25000;
  private readonly pongTimeoutMultiplier = 2;
  private lastPongAt: number | null = null;
  private lastActivityAt: number | null = null;
  private disposed = false;

  private constructor(baseHttpUrl: string) {
    this.baseHttpUrl = baseHttpUrl.replace(/\/+$/, '');
    this.wsPath = (process.env.EXPO_PUBLIC_BFF_WS_PATH ?? '/ws').replace(/^\/?/, '/').replace(/\/+$/, '');
    this.connect();
  }

  static getInstance(baseHttpUrl: string) {
    if (!BffWebSocketClient.instance) {
      BffWebSocketClient.instance = new BffWebSocketClient(baseHttpUrl);
    }
    return BffWebSocketClient.instance;
  }

  static create(baseHttpUrl: string) {
    return new BffWebSocketClient(baseHttpUrl);
  }

  dispose() {
    if (this.disposed) {
      return;
    }

    this.disposed = true;
    this.pendingReconnect = false;
    this.suppressReconnect = true;
    this.isOpen = false;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.connectTimeoutTimer) {
      clearTimeout(this.connectTimeoutTimer);
      this.connectTimeoutTimer = null;
    }
    if (this.keepAliveTimer) {
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }
    if (this.heartbeatTimeoutTimer) {
      clearTimeout(this.heartbeatTimeoutTimer);
      this.heartbeatTimeoutTimer = null;
    }

    try {
      this.socket?.close();
    } catch {
      // ignore
    }

    this.socket = null;
    this.outboundQueue.length = 0;
    this.messageHandlers.clear();
    this.connectionHandlers.clear();
    this.errorHandlers.clear();
    this.suppressReconnect = false;
  }

  batchUpdate(updater: () => void) {
    this.updateBatchDepth += 1;
    try {
      updater();
    } finally {
      this.updateBatchDepth -= 1;
      if (this.updateBatchDepth === 0 && this.pendingReconnect) {
        this.pendingReconnect = false;
        if (!this.shouldConnect()) {
          return;
        }

        if (this.isSocketActive()) {
          this.reconnect();
          return;
        }

        this.connect();
      }
    }
  }

  setAuthToken(token: string | null) {
    const previousToken = this.authToken;
    const previousTokenMeta = previousToken ? readTokenMetadata(previousToken) : null;
    const nextTokenMeta = token ? readTokenMetadata(token) : null;

    if (token && this.authToken) {
      const staleCheck = isLikelyStaleTokenUpdate(this.authToken, token);
      if (staleCheck.stale) {
        console.warn('[bff-ws] ignoring stale auth token update', {
          currentExp: staleCheck.currentExp,
          incomingExp: staleCheck.nextExp,
          currentIat: staleCheck.currentIat,
          incomingIat: staleCheck.nextIat,
        });
        return;
      }
    }
    const tokenChanged = this.authToken !== token;
    const tokenSubjectChanged =
      previousTokenMeta?.sub !== null &&
      nextTokenMeta?.sub !== null &&
      previousTokenMeta?.sub !== nextTokenMeta?.sub;

    if (token === null && previousToken !== null) {
      this.clearOutboundQueue('auth.logout');
    } else if (tokenSubjectChanged) {
      this.clearOutboundQueue('auth.subject.changed');
    }

    this.authToken = token;
    if (!this.authToken) {
      if (!this.allowAnonymousReadOnly) {
        this.suppressReconnect = true;
        try {
          this.socket?.close();
        } catch {
          // ignore
        }
        return;
      }

      if (tokenChanged) {
        this.requestReconnect();
        return;
      }

      if (this.shouldConnect() && !this.isSocketActive()) {
        this.ensureConnected();
      }
      return;
    }
    if (tokenChanged) {
      this.requestReconnect();
      return;
    }
    if (this.shouldConnect() && !this.isSocketActive()) {
      this.ensureConnected();
    }
  }

  setClaimAccessToken(token: string | null) {
    const trimmed = token?.trim() || null;
    const normalized = getUsableJwt(trimmed);
    if (trimmed && !normalized && isJwtExpired(trimmed)) {
      console.info('[bff-ws] ignoring expired claim token');
    }
    if (normalized && this.claimAccessToken) {
      const staleCheck = isLikelyStaleTokenUpdate(this.claimAccessToken, normalized);
      if (staleCheck.stale) {
        console.warn('[bff-ws] ignoring stale claim token update', {
          currentExp: staleCheck.currentExp,
          incomingExp: staleCheck.nextExp,
          currentIat: staleCheck.currentIat,
          incomingIat: staleCheck.nextIat,
        });
        return;
      }
    }
    const changed = this.claimAccessToken !== normalized;
    this.claimAccessToken = normalized;
    if (!changed) {
      return;
    }

    if (this.shouldConnect()) {
      this.requestReconnect();
    }
  }

  setInviteToken(token: string | null) {
    const normalized = token?.trim() || null;
    const changed = this.inviteToken !== normalized;
    this.inviteToken = normalized;
    if (!changed) {
      return;
    }

    if (this.shouldConnect()) {
      this.requestReconnect();
    }
  }

  setAnonymousReadOnly(allow: boolean) {
    const changed = this.allowAnonymousReadOnly !== allow;
    this.allowAnonymousReadOnly = allow;

    if (!this.allowAnonymousReadOnly && !this.authToken) {
      this.suppressReconnect = true;
      try {
        this.socket?.close();
      } catch {
        // ignore
      }
      return;
    }

    if (changed) {
      this.requestReconnect();
      return;
    }

    if (this.shouldConnect() && !this.isSocketActive()) {
      this.ensureConnected();
    }
  }

  setClaimClientId(claimClientId: string | null) {
    const normalized = claimClientId?.trim() || null;
    const changed = this.claimClientId !== normalized;
    this.claimClientId = normalized;
    if (!changed) {
      return;
    }

    if (this.shouldConnect()) {
      this.requestReconnect();
    }
  }

  setSubscriptionContext(context: { roundId?: string | null; tourId?: string | null }) {
    const nextRoundId = context.roundId ?? null;
    const nextTourId = context.tourId ?? null;
    const unchanged = this.subscribedRoundId === nextRoundId && this.subscribedTourId === nextTourId;
    this.subscribedRoundId = nextRoundId;
    this.subscribedTourId = nextTourId;
    if (this.isOpen && this.socket?.readyState === WebSocket.OPEN) {
      if (this.subscribedRoundId) {
        this.send({ type: 'round.join', roundId: this.subscribedRoundId });
        return;
      }
      if (this.subscribedTourId) {
        this.send({ type: 'tour.join', tourId: this.subscribedTourId });
        return;
      }
      this.send({ type: 'round.leave' });
      this.send({ type: 'tour.leave' });
      return;
    }
    if (this.socket?.readyState === WebSocket.CONNECTING) {
      return;
    }
    if (!unchanged) {
      this.requestReconnect();
      return;
    }
    if (this.shouldConnect() && !this.isSocketActive()) {
      this.ensureConnected();
    }
  }

  onMessage(handler: MessageHandler) {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  onConnectionChange(handler: ConnectionHandler) {
    this.connectionHandlers.add(handler);
    return () => this.connectionHandlers.delete(handler);
  }

  onError(handler: ErrorHandler) {
    this.errorHandlers.add(handler);
    return () => this.errorHandlers.delete(handler);
  }

  send(payload: Record<string, unknown>) {
    this.logDebug('send', payload);
    if (!this.socket || !this.isOpen || this.socket.readyState !== WebSocket.OPEN) {
      if (!this.isQueueableWhenDisconnected(payload)) {
        return false;
      }
      this.outboundQueue.push(payload);
      return false;
    }
    this.socket.send(JSON.stringify(payload));
    return true;
  }

  sendScoreUpdate(command: Omit<ScoreUpdateCommand, 'type'>) {
    const payload: ScoreUpdateCommand = { type: 'score.update', ...command };
    return this.send(payload);
  }

  sendUserCreate(request: { displayName: string; fullName: string; password: string; email: string; requestId?: string }) {
    return this.send({ type: 'user.create', ...request });
  }

  sendTourLeaderboardRequest(
    request: string | { tourId?: string; inviteToken?: string; requestId?: string },
    requestId?: string
  ) {
    if (typeof request === 'string') {
      return this.send({ type: 'tour.leaderboard.request', tourId: request, requestId });
    }

    return this.send({
      type: 'tour.leaderboard.request',
      tourId: request.tourId,
      inviteToken: request.inviteToken,
      requestId: request.requestId ?? requestId,
    });
  }

  sendTourInviteRequest(request: string | { tourId?: string; inviteToken?: string; requestId?: string }, requestId?: string) {
    if (typeof request === 'string') {
      return this.send({
        type: 'tour.invite.request',
        tourId: request,
        requestId,
      });
    }

    return this.send({
      type: 'tour.invite.request',
      tourId: request.tourId,
      inviteToken: request.inviteToken,
      requestId: request.requestId ?? requestId,
    });
  }

  sendTourMemberClaim(tourId: string | undefined, sourcePlayerId: string, requestId?: string, inviteToken?: string) {
    return this.send({
      type: 'tour.member.claim',
      tourId,
      sourcePlayerId,
      requestId,
      inviteToken,
    });
  }

  sendTourMemberClaimRelease(tourId: string | undefined, requestId?: string, inviteToken?: string) {
    return this.send({
      type: 'tour.member.claim.release',
      tourId,
      requestId,
      inviteToken,
    });
  }

  sendFriendListRequest(requestId?: string) {
    return this.send({
      type: 'friend.list.request',
      requestId,
    });
  }

  sendFriendAdd(request: { playerId: string; nickname?: string; requestId?: string }) {
    return this.send({
      type: 'friend.add.request',
      playerId: request.playerId,
      nickname: request.nickname,
      requestId: request.requestId,
    });
  }

  sendFriendRemove(request: { friendId: string; requestId?: string }) {
    return this.send({
      type: 'friend.remove.request',
      friendId: request.friendId,
      requestId: request.requestId,
    });
  }

  sendFriendUpdate(request: { friendId: string; nickname?: string; requestId?: string }) {
    return this.send({
      type: 'friend.update.request',
      friendId: request.friendId,
      nickname: request.nickname,
      requestId: request.requestId,
    });
  }

  sendCoachMarkStateRequest(request: { requestId?: string; keys: string[] }) {
    return this.send({
      type: 'coachMark.state.request',
      requestId: request.requestId,
      keys: request.keys,
    });
  }

  sendCoachMarkShown(request: { requestId?: string; key: string }) {
    return this.send({
      type: 'coachMark.shown',
      requestId: request.requestId,
      key: request.key,
    });
  }

  sendCoachMarkReset(requestId?: string) {
    return this.send({
      type: 'coachMark.reset',
      requestId,
    });
  }

  sendLog(entry: { message: string; attributes?: Record<string, unknown>; level?: LogEmitCommand['level'] }) {
    const payload: LogEmitCommand = {
      type: 'log.emit',
      service: 'Frontend-app',
      level: entry.level ?? 'info',
      message: entry.message,
      attributes: entry.attributes,
      timestamp: new Date().toISOString(),
    };
    return this.send(payload);
  }

  sendBugImagePresign(request: {
    requestId?: string;
    fileName?: string;
    contentType?: string;
    fileSize?: number;
  }) {
    return this.send({
      type: 'bug.image.presign',
      requestId: request.requestId,
      fileName: request.fileName,
      contentType: request.contentType,
      fileSize: request.fileSize,
    });
  }

  sendBugReport(request: {
    requestId?: string;
    description?: string;
    imageUrl?: string;
    pageUrl?: string;
    userAgent?: string;
    appVersion?: string;
    timestamp?: string;
    context?: Record<string, unknown>;
  }) {
    return this.send({
      type: 'bug.report',
      requestId: request.requestId,
      description: request.description,
      imageUrl: request.imageUrl,
      pageUrl: request.pageUrl,
      userAgent: request.userAgent,
      appVersion: request.appVersion,
      timestamp: request.timestamp,
      context: request.context,
    });
  }

  private connect() {
    if (this.disposed) {
      return;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.connectTimeoutTimer) {
      clearTimeout(this.connectTimeoutTimer);
      this.connectTimeoutTimer = null;
    }
    if (this.keepAliveTimer) {
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }
    if (this.heartbeatTimeoutTimer) {
      clearTimeout(this.heartbeatTimeoutTimer);
      this.heartbeatTimeoutTimer = null;
    }
    if (!this.shouldConnect()) {
      return;
    }
    this.isOpen = false;
    const targetSocket = new WebSocket(this.currentUrl());
    const generation = this.socketGeneration + 1;
    this.socketGeneration = generation;
    this.socket = targetSocket;
    this.scheduleConnectTimeout(targetSocket, generation);
    targetSocket.onopen = () => {
      if (!this.isCurrentSocket(targetSocket, generation)) return;
      if (this.connectTimeoutTimer) {
        clearTimeout(this.connectTimeoutTimer);
        this.connectTimeoutTimer = null;
      }
      this.isOpen = true;
      this.suppressReconnect = false;
      this.reconnectAttempt = 0;
      this.lastPongAt = Date.now();
      this.lastActivityAt = this.lastPongAt;
      if (typeof window !== 'undefined') {
        console.info('[bff-ws] connected', window.location?.pathname ?? '', window.location?.search ?? '');
      } else {
        console.info('[bff-ws] connected');
      }
      if (this.subscribedRoundId) {
        this.send({ type: 'round.join', roundId: this.subscribedRoundId });
        this.send({ type: 'round.requestState', roundId: this.subscribedRoundId });
      } else if (this.subscribedTourId) {
        this.send({ type: 'tour.join', tourId: this.subscribedTourId });
      }
      this.flushQueue();
      this.keepAliveTimer = setInterval(() => {
        if (!this.isCurrentSocket(targetSocket, generation) || !this.isOpen || targetSocket.readyState !== WebSocket.OPEN) {
          return;
        }
        try {
          const ping = { type: 'ping', timestamp: new Date().toISOString() };
          this.logDebug('send', ping);
          targetSocket.send(JSON.stringify(ping));
          this.scheduleHeartbeatTimeout(targetSocket, generation);
        } catch {
          // ignore ping failures; reconnect will handle transport issues
        }
      }, this.keepAliveIntervalMs);
      this.connectionHandlers.forEach((handler) => handler('connected'));
    };
    targetSocket.onclose = (evt) => {
      if (!this.isCurrentSocket(targetSocket, generation)) return;
      if (this.connectTimeoutTimer) {
        clearTimeout(this.connectTimeoutTimer);
        this.connectTimeoutTimer = null;
      }
      const shouldSkip = this.suppressReconnect;
      this.isOpen = false;
      this.suppressReconnect = false;
      if (this.keepAliveTimer) {
        clearInterval(this.keepAliveTimer);
        this.keepAliveTimer = null;
      }
      if (this.heartbeatTimeoutTimer) {
        clearTimeout(this.heartbeatTimeoutTimer);
        this.heartbeatTimeoutTimer = null;
      }
      if (typeof window !== 'undefined') {
        console.info(
          '[bff-ws] disconnected',
          window.location?.pathname ?? '',
          window.location?.search ?? '',
          `code=${evt.code}`,
          `reason=${evt.reason || '(none)'}`
        );
      } else {
        console.info('[bff-ws] disconnected', `code=${evt.code}`, `reason=${evt.reason || '(none)'}`);
      }
      this.connectionHandlers.forEach((handler) => handler('disconnected'));
      if (!shouldSkip) {
        this.scheduleReconnect();
      }
    };
    targetSocket.onerror = () => {
      if (!this.isCurrentSocket(targetSocket, generation)) return;
      if (this.connectTimeoutTimer) {
        clearTimeout(this.connectTimeoutTimer);
        this.connectTimeoutTimer = null;
      }
      if (this.keepAliveTimer) {
        clearInterval(this.keepAliveTimer);
        this.keepAliveTimer = null;
      }
      if (typeof window !== 'undefined') {
        console.warn('[bff-ws] error', window.location?.pathname ?? '', window.location?.search ?? '');
      } else {
        console.warn('[bff-ws] error');
      }
      const fallback = 'BFF websocket fel – försöker igen eller ladda om sidan.';
      this.errorHandlers.forEach((handler) => handler(fallback));
      this.scheduleReconnect();
    };
    targetSocket.onmessage = (evt) => {
      if (!this.isCurrentSocket(targetSocket, generation)) return;
      this.lastActivityAt = Date.now();
      try {
        const parsed = JSON.parse(String(evt.data));
        const normalized = normalizeMessage(parsed);
        this.logDebug('recv', normalized as Record<string, unknown>);
        if ((normalized as { type?: string }).type === 'pong') {
          this.lastPongAt = Date.now();
          return;
        }
        if ((normalized as { type?: string }).type === 'error' && (normalized as { message?: string }).message) {
          this.errorHandlers.forEach((handler) => handler((normalized as { message: string }).message));
        }
        this.messageHandlers.forEach((handler) => handler(normalized));
      } catch {
        this.errorHandlers.forEach((handler) => handler('Kunde inte tolka meddelande från BFF.' + evt.data ));
      }
    };
  }

  private flushQueue() {
    if (!this.socket || !this.isOpen || this.socket.readyState !== WebSocket.OPEN) return;
    while (this.outboundQueue.length > 0 && this.socket?.readyState === WebSocket.OPEN) {
      const payload = this.outboundQueue.shift();
      if (!payload) continue;
      try {
        this.logDebug('send', payload as Record<string, unknown>);
        this.socket.send(JSON.stringify(payload));
      } catch {
        // Requeue and let explicit reconnects handle retries.
        this.outboundQueue.unshift(payload);
        break;
      }
    }
  }

  private currentUrl() {
    const wsBaseOverride = process.env.EXPO_PUBLIC_BFF_WS_BASE_URL ?? process.env.EXPO_PUBLIC_API_BASE_URL;
    const baseUrl = wsBaseOverride ? toWebSocketUrl(wsBaseOverride) : toWebSocketUrl(this.baseHttpUrl);
    const base = `${baseUrl}${this.wsPath}`;
    const params: string[] = [];
    const usableClaimAccessToken = getUsableJwt(this.claimAccessToken);
    const hasClaimAccessToken = !this.authToken && !!usableClaimAccessToken;
    if (this.authToken) {
      params.push(`token=${encodeURIComponent(this.authToken)}`);
    }
    if (hasClaimAccessToken) {
      params.push(`claimToken=${encodeURIComponent(usableClaimAccessToken)}`);
    }
    if (this.subscribedRoundId) {
      params.push(`roundId=${encodeURIComponent(this.subscribedRoundId)}`);
    }
    if (this.subscribedTourId) {
      params.push(`tourId=${encodeURIComponent(this.subscribedTourId)}`);
    }
    if (this.claimClientId) {
      params.push(`claimClientId=${encodeURIComponent(this.claimClientId)}`);
    }
    if (!this.authToken && !hasClaimAccessToken && this.inviteToken) {
      params.push(`inviteToken=${encodeURIComponent(this.inviteToken)}`);
    }
    if (!this.authToken && (this.allowAnonymousReadOnly || !!this.claimClientId)) {
      params.push('anonymous=true');
    }
    return params.length ? `${base}?${params.join('&')}` : base;
  }

  private scheduleReconnect(force = false) {
    if (this.disposed) return;
    if (this.reconnectTimer) return;
    if (!this.shouldConnect()) return;
    if (
      !force &&
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    const delay = this.getReconnectDelayMs();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private shouldConnect() {
    if (this.disposed) {
      return false;
    }
    if (!this.authToken && !this.allowAnonymousReadOnly) {
      return false;
    }
    if (typeof window === 'undefined') {
      return true;
    }
    const path = window.location?.pathname ?? '';
    if (path.startsWith('/login') || path.startsWith('/callback') || path.startsWith('/register')) {
      return false;
    }
    return true;
  }

  private reconnect() {
    this.suppressReconnect = true;
    try {
      this.socket?.close();
    } catch {
      // ignore
    }
    this.suppressReconnect = false;
    this.connect();
  }

  private requestReconnect() {
    if (this.updateBatchDepth > 0) {
      this.pendingReconnect = true;
      return;
    }

    this.reconnect();
  }

  private ensureConnected() {
    if (!this.shouldConnect()) {
      return;
    }

    if (this.updateBatchDepth > 0) {
      this.pendingReconnect = true;
      return;
    }

    if (!this.isSocketActive()) {
      this.connect();
    }
  }

  private isSocketActive() {
    if (this.socket?.readyState === WebSocket.OPEN) return true;
    if (this.socket?.readyState === WebSocket.CONNECTING) return true;
    return false;
  }

  private getReconnectDelayMs() {
    const attempt = Math.min(this.reconnectAttempt, 10);
    const base = Math.min(this.reconnectMaxDelayMs, this.reconnectBaseDelayMs * Math.pow(2, attempt));
    const jitter = Math.floor(Math.random() * 250);
    this.reconnectAttempt += 1;
    return Math.min(this.reconnectMaxDelayMs, base + jitter);
  }

  private scheduleHeartbeatTimeout(targetSocket: WebSocket, generation: number) {
    if (this.heartbeatTimeoutTimer) {
      clearTimeout(this.heartbeatTimeoutTimer);
    }
    const timeoutMs = this.keepAliveIntervalMs * this.pongTimeoutMultiplier;
    this.heartbeatTimeoutTimer = setTimeout(() => {
      const last = this.lastActivityAt ?? 0;
      if (!this.isCurrentSocket(targetSocket, generation) || !this.isOpen || targetSocket.readyState !== WebSocket.OPEN) {
        return;
      }
      if (Date.now() - last >= timeoutMs) {
        this.logDebug('recv', { type: 'ws.timeout', lastActivityAt: this.lastActivityAt, lastPongAt: this.lastPongAt });
        this.reconnect();
      }
    }, timeoutMs);
  }

  private scheduleConnectTimeout(targetSocket: WebSocket, generation: number) {
    if (this.connectTimeoutTimer) {
      clearTimeout(this.connectTimeoutTimer);
    }
    this.connectTimeoutTimer = setTimeout(() => {
      this.connectTimeoutTimer = null;
      if (!this.isCurrentSocket(targetSocket, generation)) {
        return;
      }
      if (targetSocket.readyState !== WebSocket.CONNECTING || this.isOpen) {
        return;
      }
      this.logDebug('recv', {
        type: 'ws.connect.timeout',
        timeoutMs: this.connectTimeoutMs,
        reconnectAttempt: this.reconnectAttempt,
      });
      this.suppressReconnect = true;
      try {
        targetSocket.close();
      } catch {
        // ignore close failures and schedule reconnect anyway
      }
      this.suppressReconnect = false;
      this.isOpen = false;
      this.scheduleReconnect(true);
    }, this.connectTimeoutMs);
  }

  private clearOutboundQueue(reason: string) {
    const droppedCount = this.outboundQueue.length;
    if (droppedCount === 0) return;
    this.outboundQueue.length = 0;
    if (this.debugEnabled) {
      console.info('[bff-ws] outbound queue cleared', { reason, droppedCount });
    }
  }

  private isQueueableWhenDisconnected(payload: Record<string, unknown>) {
    const type = typeof payload.type === 'string' ? payload.type : '';
    return type !== 'round.leave' && type !== 'tour.leave';
  }

  private isCurrentSocket(targetSocket: WebSocket, generation: number) {
    return this.socket === targetSocket && this.socketGeneration === generation;
  }

  private logDebug(direction: 'send' | 'recv', payload: Record<string, unknown>) {
    if (!this.debugEnabled || !this.authToken) return;
    const type = (payload as { type?: string }).type;
    if (type === 'log.emit') return; // avoid infinite loop
    let serialized = '';
    try {
      serialized = JSON.stringify(payload);
    } catch {
      serialized = String(payload);
    }
    this.sendLog({
      message: `ws.${direction}.${type ?? 'unknown'}`,
      level: 'debug',
      attributes: { type, payload: serialized },
    });
  }
}

export const getSharedBffWebSocket = (baseHttpUrl: string) => BffWebSocketClient.getInstance(baseHttpUrl);
export const createBffWebSocket = (baseHttpUrl: string) => BffWebSocketClient.create(baseHttpUrl);

export const __testOnly = {
  normalizeMessage,
};
import { getUsableJwt, isJwtExpired } from '@/services/jwt';
